import React, { useCallback, useState } from 'react'
import SwiperCore, { Navigation } from 'swiper'
import classnames from 'classnames'
import message from 'antd/lib/message'

import { useAuthState, useQuery } from 'hooks'
import { apiManager } from 'helpers'
import constants from 'helpers/constants'

import Spinner from 'components/Spinner'
import ForYouProfiles from 'pages/admin/ForYouProfiles'
import DecisionModal from 'pages/admin/AdminWaitlist/DecisionModal'
import StatusFilter from 'pages/admin/AdminWaitlist/StatusFilter'
import ListView from './TableView'
import ViewTypeSwitch from './SwitchForViewType'

SwiperCore.use([Navigation])

const { MEMBERSHIP_TYPES, WAITLIST_STATUS } = constants

const PROFILE_VIEW_TYPE = {
  listView: 'List view',
  singleView: 'Single view',
}

const initialPayload = {
  decision: '',
  h_num: 0,
  isFM: false,
  cardSubmitted: undefined,
  appSubmitted: undefined,
  location: undefined,
  membershipType: undefined,
  search: '',
  currentIndexToDisplayInSingleView: 0,
}

const AdminWaitlist = () => {
  const { user } = useAuthState()
  const [status, setStatus] = useState(user.waitlistStatus)
  const [viewType, setViewType] = useState(PROFILE_VIEW_TYPE.listView)
  const [waitlistUser, setWaitlistUser] = useState()
  const [decisionCallback, setDecisionCallback] = useState()
  const [selectedUsers, setSelectedUsers] = useState([])
  const [page, setPage] = useState(1)

  const [payload, setPayload] = useState(initialPayload)

  const { isLoading, isFetching, data, error, refetch } = useQuery(
    `admin/waitlist/${status}`,
    () =>
      apiManager.getAdminWaitlist({
        page,
        waitlistStatus: status,
        paginated: true,
        cardSubmitted: payload.cardSubmitted,
        appSubmitted: payload.appSubmitted,
        search: payload.search,
        location: payload.location,
        membershipType: payload.membershipType,
      }),
    {
      enabled: user.admin,
      refetchOnWindowFocus: false,
    }
  )

  const handleAction = useCallback(async ({ liked, user }) => {
    if (!liked) {
      return true
    }

    setWaitlistUser(user)
    return new Promise(resolve => setDecisionCallback(() => resolve))
  }, [])

  const handleWaitlistDecision = useCallback(
    async payload => {
      if (!payload) {
        decisionCallback && decisionCallback()
        setWaitlistUser()
        setDecisionCallback()
        return
      }

      let completed

      try {
        await apiManager.updateWaitlistStatus({ user: waitlistUser.id, data: payload })
        setWaitlistUser()
        completed = true
      } catch (error) {
        message.error(error?.message || 'An error occurred, please try again')
      }

      decisionCallback && decisionCallback(completed)
      setDecisionCallback()
    },
    [waitlistUser, decisionCallback]
  )

  const submitBulk = useCallback(async () => {
    try {
      await Promise.all(
        selectedUsers.map(selectedUser => {
          if (payload.decision === 'skip') {
            return false
          }

          const dataToSend = {
            waitlistStatus: payload.decision,
          }

          if (selectedUser.h_num) {
            dataToSend.hNum = selectedUser.h_num
            // dataToSend.waitlistStatus = 'yes'
          }

          return apiManager.updateWaitlistStatus({
            user: selectedUser.id,
            data: dataToSend,
          })
        })
      )
      setSelectedUsers([])
      refetch()
    } catch {
      message.error('An error occurred, please try again')
    }
  }, [selectedUsers, payload, refetch])

  if (!user.admin) {
    return null
  }

  const selectUsersToUpdate = e => {
    const { name, value, checked } = e.target

    let bulk = []

    if (name === 'decision') {
      setPayload({ ...payload, decision: value })
    }

    if (name === 'h_num' || name === 'isFM') {
      if (name === 'isFM') {
        if (checked) {
          setPayload({ ...payload, decision: '', isFM: checked })
          setTimeout(() => {
            bulk = selectedUsers.map(u => {
              return {
                ...u,
                membershipType: MEMBERSHIP_TYPES.FOUNDING_MEMBER,
              }
            })
            setSelectedUsers(bulk)
          }, 0)
        } else {
          bulk = selectedUsers.map(u => {
            delete u.membershipType
            return u
          })
        }
      } else {
        setPayload({ ...payload, h_num: +value })
        if (value !== '0') {
          setTimeout(() => {
            bulk = selectedUsers.map(u => {
              return {
                ...u,
                h_num: +value,
              }
            })
            setSelectedUsers(bulk)
          }, 0)
        } else {
          bulk = selectedUsers.map(u => {
            delete u.h_num
            return u
          })
        }
      }
    } else if (name === 'id') {
      if (checked) {
        const userToAdd = {
          id: +value,
          waitlistStatus:
            status !== WAITLIST_STATUS.PRE_APPROVED
              ? WAITLIST_STATUS.PRE_APPROVED
              : WAITLIST_STATUS.YES,
        }
        if (payload.h_num) {
          userToAdd['h_num'] = +payload.h_num
        }
        if (payload.isFM) {
          userToAdd['membershipType'] = MEMBERSHIP_TYPES.FOUNDING_MEMBER
        }
        bulk = [...selectedUsers, userToAdd]
      } else {
        bulk = selectedUsers.filter(u => u.id !== +value)
      }
      setSelectedUsers(bulk)
    }
  }

  const buildViewTypeSwitch = () => {
    return <ViewTypeSwitch viewType={viewType} onFilter={setViewType} />
  }

  const handleCheckboxFilter = e => {
    const { name, checked } = e.target
    setPayload({ ...payload, [name]: checked })
    setTimeout(() => refetch(), 0)
  }

  const FilterSingleView = () => {
    return (
      <>
        <div className="flex items-center mb-4">
          <label htmlFor="default-checkbox" className="mr-2 text-sm font-medium text-white">
            Credit Card Submitted
          </label>
          <input
            checked={payload.cardSubmitted ? 'checked' : null}
            id="default-checkbox2"
            type="checkbox"
            onChange={handleCheckboxFilter}
            name="cardSubmitted"
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
        </div>
        <div className="flex items-center">
          <label htmlFor="checked-checkbox" className="mr-2 text-sm font-medium text-white">
            Application Submitted
          </label>
          <input
            checked={payload.appSubmitted ? 'checked' : null}
            id="checked-checkbox2"
            type="checkbox"
            name="appSubmitted"
            onChange={handleCheckboxFilter}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
        </div>
      </>
    )
  }

  const handleFilter = filter => {
    setPage(1)
    setPayload(() => ({ ...payload, ...filter }))
    console.log({ ...payload, ...filter })
    setTimeout(refetch, 0)
  }

  const searchItem = (event, value) => {
    event.preventDefault()

    setStatus('')
    handleFilter({ search: value })
    setTimeout(refetch, 0)
  }

  const handleSeeUserDetails = userToDisplay => {
    setPayload({
      ...payload,
      currentIndexToDisplayInSingleView: data.data.findIndex(user => user.id === userToDisplay.id),
    })
    setViewType(PROFILE_VIEW_TYPE.singleView)
  }

  const displayViews = () => {
    if (viewType !== PROFILE_VIEW_TYPE.listView) {
      const filterHidden = status !== WAITLIST_STATUS.PENDING
      return (
        <>
          {data.data && (
            <ForYouProfiles
              admin={true}
              indexToDisplay={payload.currentIndexToDisplayInSingleView}
              profiles={data.data}
              onAction={handleAction}
              onReload={refetch}
              key={status}
            >
              {!filterHidden && <FilterSingleView />}
            </ForYouProfiles>
          )}
        </>
      )
    }

    return (
      <>
        <ListView
          users={data}
          status={status}
          onChange={selectUsersToUpdate}
          payload={payload}
          submit={submitBulk}
          currentPage={page}
          isFetching={isFetching}
          onSearch={searchItem}
          onFilter={handleFilter}
          onSeeDetails={handleSeeUserDetails}
          onNext={() => {
            if (page < data.pages) {
              setPage(page + 1)
              setTimeout(refetch, 0)
            }
          }}
          onPrevious={() => {
            if (page > 1) {
              setPage(page - 1)
              setTimeout(refetch, 0)
            }
          }}
        />
      </>
    )
  }

  return (
    <div className={classnames('flex flex-col justify-between')}>
      <StatusFilter
        status={status}
        onFilter={newStatus => {
          setSelectedUsers([])
          setPayload({ ...initialPayload, search: '' })
          setStatus(newStatus)
        }}
      />
      {error ? (
        <p className="text-center text-gray-400 text-2xl">
          Something went wrong. Please, try again later!
        </p>
      ) : isLoading ? (
        <div className="flex-col justify-between h-screen-less-navbar-m md:h-screen-less-navbar flex">
          <div className="w-full flex justify-center items-center text-white flex-grow h-72 md:h-auto">
            <Spinner />
          </div>
        </div>
      ) : (
        <>
          {buildViewTypeSwitch()}
          <br />
          <br />
          {displayViews()}
        </>
      )}
      <DecisionModal
        onClose={() => handleWaitlistDecision()}
        onSave={handleWaitlistDecision}
        show={!!waitlistUser}
        waitlistStatus={waitlistUser?.waitlistStatus}
      />
    </div>
  )
}

export default AdminWaitlist
